import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CreateProofreadingRule,
  ProofreadingJob,
  ProofreadingRule,
} from '../types/proofreading-types';
import { baseQueryWithErrorHandling } from './baseQuery';

export const proofreadingApiClient = createApi({
  reducerPath: 'proofreadingApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: [
    'ProofreadingJobs',
    'ProofreadingJob',
    'ProofreadingRules',
    'StaticProofreadingRules',
    'ExportEligibility',
  ] as const,
  endpoints: (builder) => ({
    // Get all proofreading rules for a firm
    listProofreadingRules: builder.query<
      ProofreadingRule[],
      { firmId: string }
    >({
      query: ({ firmId }) => `api/firms/${firmId}/proofreading-rules/`,
      providesTags: (_result, _error, { firmId }) => [
        { type: 'ProofreadingRules', id: firmId },
      ],
    }),

    // Create a new firm-specific proofreading rule
    createProofreadingRule: builder.mutation<
      ProofreadingRule,
      { firmId: string; data: CreateProofreadingRule }
    >({
      query: ({ firmId, data }) => ({
        url: `api/firms/${firmId}/proofreading-rules/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { firmId }) => [
        { type: 'ProofreadingRules', id: firmId },
      ],
    }),

    // List STATIC_RULES catalogue (global, not firm-scoped content)
    listStaticProofreadingRules: builder.query<
      Array<{
        id: string;
        title: string;
        category: string;
        section_title: string;
        section_description: string;
        enabled: boolean;
      }>,
      { firmId: string }
    >({
      query: ({ firmId }) =>
        `api/firms/${firmId}/proofreading-rules/static-catalogue/`,
      providesTags: (_r, _e, { firmId }) => [
        { type: 'StaticProofreadingRules', id: firmId },
      ],
    }),

    // Update a proofreading rule
    updateProofreadingRule: builder.mutation<
      ProofreadingRule,
      { firmId: string; ruleId: string; data: Partial<CreateProofreadingRule> }
    >({
      query: ({ firmId, ruleId, data }) => ({
        url: `api/firms/${firmId}/proofreading-rules/${ruleId}/`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { firmId }) => [
        { type: 'ProofreadingRules', id: firmId },
      ],
    }),

    // Delete a proofreading rule
    deleteProofreadingRule: builder.mutation<
      void,
      { firmId: string; ruleId: string }
    >({
      query: ({ firmId, ruleId }) => ({
        url: `api/firms/${firmId}/proofreading-rules/${ruleId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { firmId }) => [
        { type: 'ProofreadingRules', id: firmId },
      ],
    }),

    // ---------------------------------------------------------------------
    // Proofreading Job endpoints (workspace-level)
    // ---------------------------------------------------------------------

    // List all proofreading jobs for a workspace
    listProofreadingJobs: builder.query<
      ProofreadingJob[],
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) =>
        `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/proofreading-jobs/`,
      providesTags: (result, _error, { workspaceId }) => {
        const baseTag = { type: 'ProofreadingJobs' as const, id: workspaceId };
        if (!result) return [baseTag];
        return [
          baseTag,
          ...result.map((job) => ({
            type: 'ProofreadingJob' as const,
            id: job.id,
          })),
        ];
      },
    }),

    // Create a new proofreading job for a workspace
    createProofreadingJob: builder.mutation<
      ProofreadingJob,
      { matterId: string; workspaceId: string }
    >({
      query: ({ matterId, workspaceId }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/proofreading-jobs/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { workspaceId }) => [
        { type: 'ProofreadingJobs', id: workspaceId },
      ],
    }),

    // Retrieve a specific job
    getProofreadingJob: builder.query<
      ProofreadingJob,
      { matterId: string; workspaceId: string; jobId: string }
    >({
      query: ({ matterId, workspaceId, jobId }) =>
        `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/proofreading-jobs/${jobId}/`,
      providesTags: (result) => {
        if (!result) {
          return [];
        }
        return [{ type: 'ProofreadingJob', id: result.id }];
      },
    }),

    // Rerun a proofreading job with the same rules
    rerunProofreadingJob: builder.mutation<
      ProofreadingJob,
      { matterId: string; workspaceId: string; jobId: string }
    >({
      query: ({ matterId, workspaceId, jobId }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/proofreading-jobs/${jobId}/rerun/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { workspaceId, jobId }) => [
        { type: 'ProofreadingJobs', id: workspaceId },
        { type: 'ProofreadingJob', id: jobId },
      ],
    }),

    // Update a finding's human_response
    updateFindingResponse: builder.mutation<
      { ok: boolean },
      {
        matterId: string;
        workspaceId: string;
        jobId: string;
        findingId: string;
        human_response: 'revised' | 'not_applicable' | 'incorrect' | null | '';
      }
    >({
      query: ({ matterId, workspaceId, jobId, findingId, human_response }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/proofreading-jobs/${jobId}/findings/${findingId}/`,
        method: 'PATCH',
        body: { human_response },
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: 'ProofreadingJob', id: jobId },
      ],
    }),

    // Fix in chat: create a new chat thread/message to resolve a finding
    fixInChat: builder.mutation<
      { thread_id: string; message_id: string },
      { matterId: string; workspaceId: string; findingId: string }
    >({
      query: ({ matterId, workspaceId, findingId }) => ({
        url: `api/matters/${matterId}/agent-chat-workspaces/${workspaceId}/proofreading-findings/${findingId}/fix-in-chat/`,
        method: 'POST',
      }),
    }),

    // Export eligibility for CKEditor document (gates exportWord)
    getExportEligibility: builder.query<
      { eligible: boolean; reason?: string },
      {
        documentId: string;
        matterId: string;
        workspaceId: string;
        jobId?: string;
        refreshKey?: number;
      }
    >({
      query: ({ documentId, matterId, workspaceId, jobId, refreshKey }) => {
        const base = `api/ckeditor/documents/${documentId}/export-eligibility/?matter_id=${matterId}&workspace_id=${workspaceId}`;
        const withJob = jobId ? `${base}&job_id=${jobId}` : base;
        return refreshKey != null ? `${withJob}&_=${refreshKey}` : withJob;
      },
      providesTags: (_r, _e, { documentId }) => [
        { type: 'ExportEligibility', id: documentId },
      ],
    }),
  }),
});

export const {
  useListProofreadingRulesQuery,
  useCreateProofreadingRuleMutation,
  useUpdateProofreadingRuleMutation,
  useDeleteProofreadingRuleMutation,
  useListStaticProofreadingRulesQuery,
  useListProofreadingJobsQuery,
  useCreateProofreadingJobMutation,
  useRerunProofreadingJobMutation,
  useGetProofreadingJobQuery,
  useUpdateFindingResponseMutation,
  useFixInChatMutation,
  useGetExportEligibilityQuery,
} = proofreadingApiClient;
